import { useState } from "react";
import AppImage from '@/components/AppImage';
import styles from "./HeroSection.module.css";
import { useAuth } from "../context/AuthContext";

// Keep the decorativeBooks array as a fallback - now using local optimized images
const defaultDecorativeBooks = [
  {
    title: "Fourth Wing",
    coverUrl: "/images/books/fourth-wing.jpg",
    row: 0,
  },
  {
    title: "Iron Flame",
    coverUrl: "/images/books/iron-flame.jpg",
    row: 0,
  },
  {
    title: "Tomorrow x3",
    coverUrl: "/images/books/tomorrow-x3.jpg",
    row: 0,
  },
  {
    title: "A Court of Thorns and Roses",
    coverUrl: "/images/books/court-thorns-roses.jpg",
    row: 1,
  },
  {
    title: "House of Earth and Blood",
    coverUrl: "/images/books/house-earth-blood.jpg",
    row: 1,
  },
  {
    title: "The Way of Kings",
    coverUrl: "/images/books/fourth-wing.jpg",
    row: 1,
  },
  {
    title: "Book 7",
    coverUrl: "/images/books/book-unknown-1.jpg",
    row: 1,
  },
  {
    title: "Book 8",
    coverUrl:
      "/images/books/book-unknown-3.jpg",
    row: 1,
  },
  {
    title: "Book 9",
    coverUrl:
      "/images/books/book-unknown-4.jpg",
    row: 1,
  },
  {
    title: "Book 10",
    coverUrl:
      "/images/books/book-unknown-4.jpg",
    row: 1,
  },
  {
    title: "Book 11",
    coverUrl:
      "/images/books/book-unknown-4.jpg",
    row: 1,
  },
  {
    title: "Book 12",
    coverUrl: "/images/books/book-unknown-4.jpg",
    row: 1,
  },
  {
    title: "Book 13",
    coverUrl: "/images/books/book-unknown-5.jpg",
    row: 1,
  },
  {
    title: "Book 14", 
    coverUrl: "/images/books/book-unknown-6.jpg",
    row: 1,
  },
  {
    title: "Book 15",
    coverUrl: "/images/books/book-unknown-7.jpg", 
    row: 1,
  },
  {
    title: "Book 16",
    coverUrl: "/images/books/book-unknown-8.jpg",
    row: 1,
  },
  {
    title: "Book 17",
    coverUrl: "/images/books/book-unknown-9.jpg",
    row: 1,
  },
  {
    title: "Book 18", 
    coverUrl: "/images/books/book-unknown-10.jpg",
    row: 1,
  },
  {
    title: "Book 19",
    coverUrl: "/images/books/book-unknown-11.jpg",
    row: 1,
  },
  {
    title: "Book 20",
    coverUrl: "/images/books/book-unknown-12.jpg", 
    row: 1,
  },
  {
    title: "Book 21",
    coverUrl: "/images/books/book-unknown-13.jpg",
    row: 1,
  },
  {
    title: "Book 22", 
    coverUrl: "/images/books/book-unknown-14.jpg",
    row: 1,
  },
];

export default function HeroSection({ content }) {
  const [books] = useState(() => defaultDecorativeBooks);
  const { user } = useAuth();

  // Split books array into two halves
  const midPoint = Math.ceil(books.length / 2);
  const firstHalf = books.slice(0, midPoint);
  const secondHalf = books.slice(midPoint);

  // Use content from props if available, otherwise use defaults
  const heroContent = content || {
    currentContent: "Your Reading Journey,\nBeautifully Tracked",
    subtitle:
      "Join the world's most passionate reading community. Track your books, share thoughtful reviews, and discover your next favorite read.",
    ctaButton: {
      text: "Start Your Reading Journey",
      link: "/signup",
    },
  };

  const calculateBookPosition = (index, total) => {
    const radius = 500;
    const theta = (2 * Math.PI * index) / total;
    const y = Math.sin(theta) * radius;
    const z = Math.cos(theta) * radius;

    return {
      transform: `
        translate(-50%, -50%)
        translateY(${y}px)
        translateZ(${z}px)
        rotateX(${-(index * 360) / total}deg)
      `,
    };
  };

  // Split the main title on line breaks if present
  const titleLines = heroContent.currentContent.split("\n");

  return (
    <section className={styles.heroSection}>
      <div className={styles.mainContent}>
        <div className={styles.valueProposition}>
          <h1 className={styles.mainTitle}>
            {titleLines.map((line, index) => (
              <span key={index}>
                {line}
                {index < titleLines.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p className={styles.subtitle}>{heroContent.subtitle}</p>

          <button
            className={styles.ctaButton}
            onClick={() =>
              heroContent.ctaButton.link
                ? user
                  ? (window.location.href = "/search")
                  : (window.location.href = heroContent.ctaButton.link)
                : null
            }
          >
            {heroContent.ctaButton.text}
          </button>
        </div>

        <div className={styles.bookDisplayContainer}>
          <div className={styles.bookDisplayWrapper}>
            <div className={styles.bookDisplay}>
              {firstHalf.map((book, index) => (
                <div
                  key={`primary-${book.title}`}
                  className={styles.bookCover}
                  style={calculateBookPosition(index, firstHalf.length)}
                >
                  <AppImage 
                    src={book.coverUrl} 
                    alt={book.title}
                    width={160}
                    height={240}
                    priority={index < 2}
                    loading={index < 2 ? "eager" : "lazy"}
                    sizes="(max-width: 768px) 120px, 160px"
                  />
                  <div className={styles.spotlight} />
                </div>
              ))}
            </div>
          </div>

          <div className={`${styles.bookDisplayWrapper} ${styles.secondary}`}>
            <div className={`${styles.bookDisplay} ${styles.reverseRotation}`}>
              {secondHalf.map((book, index) => (
                <div
                  key={`secondary-${book.title}`}
                  className={styles.bookCover}
                  style={calculateBookPosition(index, secondHalf.length)}
                >
                  <AppImage 
                    src={book.coverUrl} 
                    alt={book.title}
                    width={160}
                    height={240}
                    loading="lazy"
                    sizes="(max-width: 768px) 120px, 160px"
                  />
                  <div className={styles.spotlight} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
